export interface LibrarySearchHit {
    themeName: string;
    subTopicName: string;
    youtubeLink: string;
}

export interface MergedLibrarySearchHit {
    themeNames: string[];
    subTopicName: string;
    youtubeLink: string;
}

function parseStartSeconds(raw: string | null): number {
    if (!raw) return 0;

    const value = raw.trim();
    if (/^\d+$/.test(value)) return Number(value);
    if (/^\d+s$/i.test(value)) return Number(value.slice(0, -1));

    const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
    if (!match || (!match[1] && !match[2] && !match[3])) return 0;

    return Number(match[1] ?? 0) * 3600 + Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0);
}

/** Same class moment across theme copies: video id plus start time. */
export function youtubeMomentKey(url: string): string | null {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }

    const host = parsed.hostname.replace(/^www\./, '');
    let id: string | null = null;

    if (host === 'youtu.be') {
        id = parsed.pathname.split('/').filter(Boolean)[0] ?? null;
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
        if (parsed.pathname === '/watch') {
            id = parsed.searchParams.get('v');
        } else {
            const [kind, videoId] = parsed.pathname.split('/').filter(Boolean);
            if (kind === 'live' || kind === 'embed' || kind === 'shorts') {
                id = videoId ?? null;
            }
        }
    }

    if (!id) return null;

    const seconds = parseStartSeconds(parsed.searchParams.get('t') ?? parsed.searchParams.get('start'));
    return `${id}:${seconds}`;
}

/**
 * Keyword search lists each matching moment once.
 * Theme chips come from every theme that stores that same video and start time.
 * The title is the first hit that matched the query.
 */
export function mergeLibrarySearchHits(
    hits: LibrarySearchHit[],
    catalog: LibrarySearchHit[],
): MergedLibrarySearchHit[] {
    const themesByMoment = new Map<string, string[]>();

    catalog.forEach((entry) => {
        const key = youtubeMomentKey(entry.youtubeLink);
        if (!key) return;

        const themes = themesByMoment.get(key) ?? [];
        if (!themes.includes(entry.themeName)) {
            themes.push(entry.themeName);
        }
        themesByMoment.set(key, themes);
    });

    const seen = new Set<string>();
    const merged: MergedLibrarySearchHit[] = [];

    hits.forEach((hit, index) => {
        const key = youtubeMomentKey(hit.youtubeLink);
        const dedupeKey = key ?? `unparsed:${index}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);

        merged.push({
            themeNames: key ? (themesByMoment.get(key) ?? [hit.themeName]) : [hit.themeName],
            subTopicName: hit.subTopicName,
            youtubeLink: hit.youtubeLink,
        });
    });

    return merged;
}
